import { useCallback, useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Show, UserButton, useAuth } from "@clerk/react";
import RequestCard from "@/components/owner/RequestCard";
import UsersPanel from "@/components/owner/UsersPanel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function Dashboard() {
  const { getToken } = useAuth();
  const [role, setRole] = useState(null);
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");

  const call = useCallback(async (method, path, data) => {
    const headers = { Authorization: `Bearer ${await getToken()}` };
    return (await axios({ method, url: `${API}${path}`, data, headers })).data;
  }, [getToken]);

  useEffect(() => {
    (async () => {
      try {
        const me = await call("get", "/me");
        setRole(me.role);
        if (me.role === "admin") setItems(await call("get", "/requests"));
      } catch { setRole("error"); }
    })();
  }, [call]);

  const updateStatus = async (id, status) => {
    try {
      const updated = await call("patch", `/requests/${id}`, { status });
      setItems((xs) => xs.map((x) => (x.id === id ? updated : x)));
      toast.success("Status updated");
    } catch { toast.error("Could not update status"); }
  };

  if (!role) return <p className="p-10 text-gold-soft" data-testid="owner-loading">Loading…</p>;
  if (role === "error") return <p className="p-10 text-[#f7f1e1]/80" data-testid="owner-error">Could not load this page. Please refresh to try again.</p>;
  if (role !== "admin") return <p className="p-10 text-[#f7f1e1]/80" data-testid="owner-no-access">This page is only for the site owner.</p>;

  const shown = filter === "all" ? items : items.filter((x) => x.status === filter);
  return (
    <div className="px-6 md:px-16 py-10 max-w-5xl mx-auto">
      <h1 className="font-display text-3xl md:text-5xl gold-text">Session Requests</h1>
      <p className="text-[#f7f1e1]/70 mt-3" data-testid="owner-count">{items.length} total · {items.filter((x) => x.status === "new").length} new</p>
      <div className="flex flex-wrap gap-2 mt-8">
        {["all", "new", "contacted", "completed"].map((f) => (
          <button key={f} onClick={() => setFilter(f)} data-testid={`filter-${f}`}
            className={`rounded-full px-5 py-2 font-heading text-xs tracking-widest border transition-colors ${filter === f ? "bg-[#d4af37] text-[#070d2b] border-[#d4af37]" : "border-[#d4af37]/40 text-gold-soft"}`}>
            {f.toUpperCase()}
          </button>
        ))}
      </div>
      <div className="space-y-5 mt-8" data-testid="requests-list">
        {shown.length === 0 && <p className="text-[#f7f1e1]/60" data-testid="requests-empty">No requests here yet.</p>}
        {shown.map((r) => <RequestCard key={r.id} r={r} onStatus={updateStatus} />)}
      </div>
      <UsersPanel call={call} />
    </div>
  );
}

export default function Owner() {
  return (
    <main className="starfield min-h-screen" data-testid="owner-page">
      <header className="flex justify-between items-center px-6 md:px-16 py-6 border-b border-[#d4af37]/20">
        <Link to="/" data-testid="owner-home-link" className="font-heading tracking-[0.3em] text-xs text-gold">← EARTH ANGEL</Link>
        <Show when="signed-in"><UserButton /></Show>
      </header>
      <Show when="signed-in"><Dashboard /></Show>
      <Show when="signed-out"><Navigate to="/sign-in" replace /></Show>
    </main>
  );
}
